import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { corpusApi, Corpus } from '../api/corpora'
import { useAuth } from './AuthContext'

const ACTIVE_CORPUS_KEY = 'pmc_active_corpus'

interface CorpusContextType {
  corpora: Corpus[]
  activeCorpusId: string
  activeCorpus: Corpus | null
  setActiveCorpusId: (id: string) => void
  refreshCorpora: () => Promise<void>
}

const CorpusContext = createContext<CorpusContextType | null>(null)

export function CorpusProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth()
  const [corpora, setCorpora] = useState<Corpus[]>([])
  const [activeCorpusId, setActiveCorpusIdState] = useState<string>(
    () => localStorage.getItem(ACTIVE_CORPUS_KEY) || ''
  )

  const setActiveCorpusId = useCallback((id: string) => {
    setActiveCorpusIdState(id)
    if (id) localStorage.setItem(ACTIVE_CORPUS_KEY, id)
    else localStorage.removeItem(ACTIVE_CORPUS_KEY)
  }, [])

  const refreshCorpora = useCallback(async () => {
    try {
      const res = await corpusApi.list()
      setCorpora(res.data)
      // Pick the active database: keep a still-valid current selection, else the
      // user's preferred default (if accessible), else auto-pick a lone database.
      setActiveCorpusIdState(prev => {
        if (prev && res.data.some(c => c.corpus_id === prev)) return prev
        const pref = user?.default_corpus_id
        if (pref && res.data.some(c => c.corpus_id === pref)) {
          localStorage.setItem(ACTIVE_CORPUS_KEY, pref)
          return pref
        }
        if (res.data.length === 1) {
          localStorage.setItem(ACTIVE_CORPUS_KEY, res.data[0].corpus_id)
          return res.data[0].corpus_id
        }
        return prev
      })
    } catch {
      // silent — keep existing
    }
  }, [isAuthenticated, user?.default_corpus_id])

  useEffect(() => {
    if (isAuthenticated) refreshCorpora()
    else { setCorpora([]); setActiveCorpusId('') }
  }, [isAuthenticated, refreshCorpora, setActiveCorpusId])

  // Drop a stale persisted id if it no longer exists.
  useEffect(() => {
    if (activeCorpusId && corpora.length > 0 && !corpora.some(c => c.corpus_id === activeCorpusId)) {
      setActiveCorpusId('')
    }
  }, [corpora, activeCorpusId, setActiveCorpusId])

  const activeCorpus = corpora.find(c => c.corpus_id === activeCorpusId) || null

  return (
    <CorpusContext.Provider
      value={{ corpora, activeCorpusId, activeCorpus, setActiveCorpusId, refreshCorpora }}
    >
      {children}
    </CorpusContext.Provider>
  )
}

export function useCorpus() {
  const ctx = useContext(CorpusContext)
  if (!ctx) throw new Error('useCorpus must be used within CorpusProvider')
  return ctx
}
